<!-- Panel for selecting a single entity or application focus in the BPMN diagram. -->
<!-- One button covers both focus types; only one can be active at a time. -->
<template>
    <template
        v-if="focusableEntities.length > 0 || focusableApplications.length > 0"
    >
        <Button
            ref="triggerRef"
            v-tooltip.top="{
                value: 'Highlight items that interact with a domain object or app',
                showDelay: 200,
            }"
            class="focus-trigger"
            :class="{ active: isActive }"
            severity="secondary"
            size="small"
            :icon="activeIcon"
            :label="activeLabel"
            :style="isActive ? { '--focus-color': activeColor } : {}"
            @click="popoverRef.toggle($event)"
        />
        <Popover
            ref="popoverRef"
            id="focus_panel_popover"
            @hide="searchQuery = ''"
        >
            <div class="focus-pills">
                <small class="focus-description">
                    Select a domain object or app to highlight anything that
                    uses it.
                </small>
                <InputText
                    v-model="searchQuery"
                    placeholder="Search..."
                    size="small"
                    class="focus-search"
                    autofocus
                />
                <template v-if="filteredEntities.length > 0">
                    <div v-if="showSectionHeaders" class="focus-section-label">
                        Domain object
                    </div>
                    <button
                        v-for="entity in filteredEntities"
                        :key="entity.entity_id"
                        class="focus-pill"
                        :class="{
                            active: focusedEntityId === entity.entity_id,
                        }"
                        :style="
                            focusedEntityId === entity.entity_id
                                ? {
                                      backgroundColor: entityColor,
                                      borderColor: entityColor,
                                  }
                                : {}
                        "
                        @click="toggleEntity(entity.entity_id)"
                    >
                        <span class="focus-pill-name">{{ entity.name }}</span>
                        <i
                            v-if="focusedEntityId === entity.entity_id"
                            class="pi pi-times focus-pill-clear"
                        />
                    </button>
                </template>
                <div v-if="showSectionHeaders" class="focus-section-divider" />
                <template v-if="filteredApplications.length > 0">
                    <div v-if="showSectionHeaders" class="focus-section-label">
                        App
                    </div>
                    <button
                        v-for="app in filteredApplications"
                        :key="app.identity"
                        class="focus-pill"
                        :class="{
                            active: focusedApplicationId === app.identity,
                        }"
                        :style="
                            focusedApplicationId === app.identity
                                ? {
                                      backgroundColor: appColor,
                                      borderColor: appColor,
                                  }
                                : {}
                        "
                        @click="toggleApplication(app.identity)"
                    >
                        <span class="focus-pill-name">{{ app.name }}</span>
                        <i
                            v-if="focusedApplicationId === app.identity"
                            class="pi pi-times focus-pill-clear"
                        />
                    </button>
                </template>
            </div>
        </Popover>
    </template>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import { computed, ref } from "vue";

import { useHotKeys } from "@/composables/hotkeys";

const props = defineProps<{
    focusableEntities: { entity_id: string; name: string }[];
    focusedEntityId: string | null;
    focusableApplications: { identity: string; name: string }[];
    focusedApplicationId: string | null;
}>();

const emit = defineEmits<{
    "update:focusedEntityId": [entityId: string | null];
    "update:focusedApplicationId": [applicationId: string | null];
}>();

const popoverRef = ref<InstanceType<typeof Popover>>();
const triggerRef = ref<InstanceType<typeof Button>>();

const { addHotKey } = useHotKeys();
addHotKey(
    "f",
    (event) => {
        event.preventDefault();
        popoverRef.value?.toggle({
            currentTarget: triggerRef.value?.$el,
        } as Event);
    },
    "Toggle focus panel",
    "BPMN"
);

const searchQuery = ref("");

const filteredEntities = computed(() => {
    if (!searchQuery.value) return props.focusableEntities;
    const q = searchQuery.value.toLowerCase();
    return props.focusableEntities.filter((e) =>
        e.name.toLowerCase().includes(q)
    );
});

const filteredApplications = computed(() => {
    if (!searchQuery.value) return props.focusableApplications;
    const q = searchQuery.value.toLowerCase();
    return props.focusableApplications.filter((a) =>
        a.name.toLowerCase().includes(q)
    );
});

const entityColor = computed(() =>
    window
        .getComputedStyle(document.body)
        .getPropertyValue("--stekz-linkedentities-color")
        .trim()
);

const appColor = computed(() =>
    window
        .getComputedStyle(document.body)
        .getPropertyValue("--stekz-applications-color")
        .trim()
);

const showSectionHeaders = computed(
    () =>
        filteredEntities.value.length > 0 &&
        filteredApplications.value.length > 0
);

const isActive = computed(
    () => !!props.focusedEntityId || !!props.focusedApplicationId
);

const activeColor = computed(() => {
    if (props.focusedEntityId) return entityColor.value;
    if (props.focusedApplicationId) return appColor.value;
    return "";
});

const activeIcon = computed(() => {
    if (props.focusedEntityId) return "pi pi-building";
    if (props.focusedApplicationId) return "pi pi-desktop";
    return "pi pi-eye";
});

const activeLabel = computed(() => {
    if (props.focusedEntityId) {
        const name = props.focusableEntities.find(
            (e) => e.entity_id === props.focusedEntityId
        )?.name;
        return name ? `Focus: ${name}` : "Focus";
    }
    if (props.focusedApplicationId) {
        const name = props.focusableApplications.find(
            (a) => a.identity === props.focusedApplicationId
        )?.name;
        return name ? `Focus: ${name}` : "Focus";
    }
    return "Focus";
});

const toggleEntity = (entityId: string) => {
    emit(
        "update:focusedEntityId",
        props.focusedEntityId === entityId ? null : entityId
    );
    popoverRef.value?.hide();
};

const toggleApplication = (applicationId: string) => {
    emit(
        "update:focusedApplicationId",
        props.focusedApplicationId === applicationId ? null : applicationId
    );
    popoverRef.value?.hide();
};
</script>

<style scoped lang="scss">
.focus-trigger {
    text-decoration: none;

    &.active {
        --p-button-secondary-background: var(--focus-color);
        --p-button-secondary-border-color: var(--focus-color);
        --p-button-secondary-color: rgba(255, 255, 255, 0.85);
        --p-button-secondary-hover-background: var(--focus-color);
        --p-button-secondary-hover-border-color: var(--focus-color);
        --p-button-secondary-hover-color: rgba(255, 255, 255, 0.85);

        &:hover {
            opacity: 0.85;
        }
    }
}

.focus-pills {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    min-width: 12rem;
}

.focus-description {
    font-size: 11px;
    color: var(--p-text-muted-color);
    margin: 0;
    line-height: 1.4;
    max-width: 12rem;
}

.focus-search {
    width: 100%;
    font-size: 12px;
}

.focus-section-label {
    font-size: 10px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--p-text-muted-color);
    padding: 0 2px;
}

.focus-section-divider {
    height: 1px;
    background: var(--p-content-border-color);
    margin: 2px 0;
}

.focus-pill {
    width: 100%;
    padding: 3px 10px;
    border: 1px solid var(--p-content-border-color);
    background: transparent;
    color: var(--p-text-muted-color);
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        background: var(--surface-background-mild);
        color: var(--p-content-color);
    }

    &.active {
        color: rgba(255, 255, 255, 0.75);

        &:hover {
            opacity: 0.9;
        }
    }
}

.focus-pill-name {
    flex: 1 1 auto;
    text-align: left;
}

.focus-pill-clear {
    font-size: 10px;
    opacity: 0.7;
    flex-shrink: 0;
}
</style>

<!-- Unscoped: Popover teleports to body, so the scoped attribute can't reach it.
     Targeted by id to avoid affecting other Popover instances. -->
<style lang="scss">
#focus_panel_popover {
    margin-block-start: 0;

    &.p-popover::before,
    &.p-popover::after {
        display: none;
    }
}
</style>
